export const LINK_QUALITY_LABEL = {
    utmerket: 'Utmerket', god: 'God', marginal: 'Marginal', darlig: 'Dårlig', ingen: 'Ingen link',
};
export const LINK_QUALITY_COLOR = {
    utmerket: '#4bd15b', god: '#9acd32', marginal: '#ffd24a', darlig: '#ff8a3d', ingen: '#ff3b3b',
};
const QUALITY_ORDER = ['ingen', 'darlig', 'marginal', 'god', 'utmerket'];
function worst(a, b) {
    return QUALITY_ORDER.indexOf(a) <= QUALITY_ORDER.indexOf(b) ? a : b;
}
/** RSSI (dBm) → nivå. Typiske terskler for smalbånds-radio/LoRa-lignende. */
export function rssiTier(dbm) {
    if (dbm === undefined || !Number.isFinite(dbm))
        return 'ingen';
    if (dbm >= -70)
        return 'utmerket';
    if (dbm >= -85)
        return 'god';
    if (dbm >= -100)
        return 'marginal';
    if (dbm >= -115)
        return 'darlig';
    return 'ingen';
}
/** SNR (dB) → nivå. */
export function snrTier(db) {
    if (db === undefined || !Number.isFinite(db))
        return 'ingen';
    if (db >= 10)
        return 'utmerket';
    if (db >= 5)
        return 'god';
    if (db >= 0)
        return 'marginal';
    if (db >= -5)
        return 'darlig';
    return 'ingen';
}
/** Samlet link-kvalitet: dårligste av RSSI og SNR (fail-safe pessimistisk). */
export function linkQuality(mesh) {
    if (!mesh)
        return 'ingen';
    return worst(rssiTier(mesh.rssi_dbm), snrTier(mesh.snr_db));
}
/** Dyp hop-terskel: like mange/flere hopp = sårbar (langt fra gateway). */
export const DEEP_HOP = 4;
/** Vurder én node. `neighbors`-antall brukes når neighbor_ids ikke er rapportert. */
export function nodeMeshStatus(id, mesh) {
    const quality = linkQuality(mesh);
    const nbCount = mesh?.neighbor_ids?.length ?? mesh?.neighbors;
    const isolated = nbCount === 0;
    const deepHop = (mesh?.hops ?? 0) >= DEEP_HOP;
    const reasons = [];
    if (quality === 'ingen' || quality === 'darlig')
        reasons.push(`link ${LINK_QUALITY_LABEL[quality].toLowerCase()}`);
    if (isolated)
        reasons.push('ingen naboer');
    if (deepHop)
        reasons.push(`dyp hop (${mesh?.hops})`);
    return { id, quality, isolated, deepHop, reasons };
}
/** Roll-up over hele meshet til befalets helse-panel. */
export function rollupMesh(nodes) {
    const byQuality = { utmerket: 0, god: 0, marginal: 0, darlig: 0, ingen: 0 };
    const isolated = [];
    const deepHop = [];
    let maxHops = 0;
    let weakest = null;
    for (const n of nodes) {
        const s = nodeMeshStatus(n.id, n.mesh);
        byQuality[s.quality]++;
        if (s.isolated)
            isolated.push(s.id);
        if (s.deepHop)
            deepHop.push(s.id);
        maxHops = Math.max(maxHops, n.mesh?.hops ?? 0);
        if (!weakest ||
            QUALITY_ORDER.indexOf(s.quality) < QUALITY_ORDER.indexOf(weakest.quality) ||
            (s.quality === weakest.quality && s.reasons.length > weakest.reasons.length)) {
            weakest = s;
        }
    }
    return { total: nodes.length, byQuality, isolated, deepHop, maxHops, weakest };
}
/**
 * Bygg en urettet topologi-graf fra nodenes rapporterte neighbor_ids.
 * Kanter dedupliseres (a<b) og lenker mot ukjente noder tas med (noden legges til).
 */
export function buildMeshTopology(nodes) {
    const nodeSet = new Set(nodes.map((n) => n.id));
    const adj = new Map();
    const link = (a, b) => {
        if (a === b)
            return;
        if (!adj.has(a))
            adj.set(a, new Set());
        if (!adj.has(b))
            adj.set(b, new Set());
        adj.get(a).add(b);
        adj.get(b).add(a);
    };
    for (const n of nodes) {
        nodeSet.add(n.id);
        for (const nb of n.mesh?.neighbor_ids ?? []) {
            nodeSet.add(nb);
            link(n.id, nb);
        }
    }
    const allNodes = [...nodeSet].sort();
    const edges = [];
    const seen = new Set();
    for (const [a, nbs] of adj) {
        for (const b of nbs) {
            const key = a < b ? `${a} ${b}` : `${b} ${a}`;
            if (seen.has(key))
                continue;
            seen.add(key);
            edges.push(a < b ? { a, b } : { a: b, b: a });
        }
    }
    const isolated = allNodes.filter((id) => !(adj.get(id)?.size));
    // Sammenhengende komponenter (BFS).
    const visited = new Set();
    const partitions = [];
    for (const start of allNodes) {
        if (visited.has(start))
            continue;
        const comp = [];
        const queue = [start];
        visited.add(start);
        while (queue.length) {
            const cur = queue.shift();
            comp.push(cur);
            for (const nb of adj.get(cur) ?? []) {
                if (!visited.has(nb)) {
                    visited.add(nb);
                    queue.push(nb);
                }
            }
        }
        partitions.push(comp.sort());
    }
    return { nodes: allNodes, edges, isolated, partitions };
}
//# sourceMappingURL=meshhealth.js.map